import React, { useState, useCallback, useEffect } from 'react';
import { FiUserPlus, FiLink, FiTrash2, FiCopy, FiCheck } from 'react-icons/fi';
import * as sched from 'sched';
import InviteUserModal from '../../components/modals/InviteUserModal';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '@/context/WorkspaceContext';

const InvitationsPage: React.FC = () => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [tokens, setTokens] = useState<sched.Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { client, token } = useAuth();
  const { workspaceId } = useWorkspace();

  const loadTokens = useCallback(async () => {
    if (!client || !token || !workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await sched.WrappedApiClient.list_workspace_tokens(
        client,
        token,
        workspaceId,
      );
      setTokens(response.tokens);
    } catch (err) {
      setError(`Failed to load invite tokens: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [client, token, workspaceId]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleDelete = async (tokenId: string) => {
    if (!client || !token || !workspaceId) return;
    if (!window.confirm('Revoke this invite link? Anyone with the link will no longer be able to join.')) return;

    try {
      await sched.WrappedApiClient.delete_workspace_token(
        client,
        token,
        workspaceId,
        tokenId,
      );
      await loadTokens();
    } catch (err) {
      setError(`Failed to delete token: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const copyInviteUrl = async (tokenId: string) => {
    const url = `${window.location.origin}/invite/${tokenId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(tokenId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const formatPermissions = (authorization: sched.TokenAuthorization): string => {
    if (authorization.type === 'InviteToWorkspace') {
      return authorization.permissions.map((p: sched.WorkspacePermission) => p.type).join(', ');
    }
    return '';
  };

  const formatExpiration = (expiration: string | null): string => {
    if (!expiration) return 'Never';
    const date = new Date(expiration);
    if (date < new Date()) return 'Expired';
    return date.toLocaleDateString();
  };

  const handleInviteModalClose = () => {
    setShowInviteModal(false);
    loadTokens();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Invitations</h1>
          <p className="mt-1 text-sm text-cm-text-secondary">
            Invite users to this workspace and manage existing invite links.
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cm-accent-orange text-cm-bg-deep font-mono text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <FiUserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiLink className="w-5 h-5 text-cm-accent-blue" />
          <h2 className="font-mono font-medium text-cm-text-primary">Invite Links</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <p className="text-cm-text-muted text-sm font-mono">Loading...</p>
          </div>
        ) : tokens.length === 0 ? (
          <div className="flex items-center justify-center h-32 border border-dashed border-cm-border-mid rounded-lg">
            <p className="text-cm-text-muted text-sm font-mono">
              No active invite links. Click "Invite User" to create one.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-cm-border-subtle">
            {tokens.map((t) => (
              <div key={t.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm text-cm-text-primary truncate">
                    {`${window.location.origin}/invite/${t.id}`}
                  </div>
                  <div className="flex gap-3 mt-1 text-xs text-cm-text-muted font-mono">
                    <span>Permissions: {formatPermissions(t.authorization)}</span>
                    <span>Expires: {formatExpiration(t.expiration)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => copyInviteUrl(t.id)}
                    className="p-2 rounded-lg text-cm-text-secondary hover:text-cm-text-primary hover:bg-cm-bg-raised transition-colors"
                    title="Copy invite link"
                  >
                    {copiedId === t.id ? (
                      <FiCheck className="w-4 h-4 text-green-400" />
                    ) : (
                      <FiCopy className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-2 rounded-lg text-cm-text-secondary hover:text-red-400 hover:bg-cm-bg-raised transition-colors"
                    title="Revoke invite link"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <InviteUserModal
        isOpen={showInviteModal}
        onClose={handleInviteModalClose}
      />
    </div>
  );
};

export default InvitationsPage;
